import React, { useState, useEffect, useContext, useRef } from 'react';
import { FaComments, FaUser, FaPaperPlane, FaSearch, FaStar, FaGraduationCap, FaClock, FaCircle, FaUsers, FaBars, FaTimes, FaExpand, FaCompress } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { fetchTeachers } from '../../redux/slices/teachersSlice';
import { getTeacherStudentsByClass } from '../../redux/slices/lessonsSlice';
import { makeConversation, getConversation, sendMessage } from '../../redux/slices/conversationsSlice';
import { ref, push, set, onValue, off, serverTimestamp } from 'firebase/database';
import { database } from '../../firebase/config';
import styles from './ChatWithTeacher.module.css';

const ChatWithTeacher = () => {
    console.log('=== ChatWithTeacher Component Loaded ===');
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.auth);
    const { teachers, loading, error } = useSelector((state) => state.teachers);
    const { teacherStudents, studentsLoading, studentsError } = useSelector((state) => state.lessons);
    const { 
        currentConversation, 
        messages, 
        conversationLoading, 
        conversationError,
        sendMessageLoading,
        sendMessageError,
        makeConversationLoading,
        makeConversationError
    } = useSelector((state) => state.conversations);
    

    
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    
    // Messages state - دمج API + Firebase
    const [allMessages, setAllMessages] = useState([]);
    const [isFirebaseListening, setIsFirebaseListening] = useState(false);
    const [firebaseError, setFirebaseError] = useState(null);
    
    // Local conversation state
    const [localConversation, setLocalConversation] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    
    // Fullscreen state
    const [isChatFullscreen, setIsChatFullscreen] = useState(false);
    
    // Ref للـ messages container
    const messagesContainerRef = useRef(null);

    // التحقق من نوع المستخدم
    useEffect(() => {
        if (currentUser?.role === 'teacher') {
            // إذا كان المستخدم معلم، جلب طلاب الفصل
            dispatch(getTeacherStudentsByClass({ classId: 0, page: 1, perPage: 15 }));
        } else {
            // جلب المدرسين من API للطالب وولي الأمر
            dispatch(fetchTeachers());
        }
    }, [dispatch, currentUser]);

    // لا نحتاج هذا useEffect لأن handleSelectPerson يقوم بإنشاء المحادثة

    // دالة auto-scroll للرسائل الجديدة
    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            // تأخير بسيط للتأكد من أن الرسائل تم عرضها
            setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                    console.log('Scrolled to bottom, scrollHeight:', messagesContainerRef.current.scrollHeight);
                }
            }, 100);
        }
    };
    
    // دالة تحميل المزيد من الرسائل
    const loadMoreMessages = async () => {
        if (!hasMore || !localConversation?.id) return;
        
        const nextPage = currentPage + 1;
        try {
            const result = await dispatch(getConversation({ 
                conversation_id: localConversation.id, 
                page: nextPage 
            })).unwrap();
            
            if (result?.result?.pagination?.current_page < result?.result?.pagination?.pages_count) {
                setCurrentPage(result.result.pagination.current_page);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('خطأ في تحميل المزيد من الرسائل:', error);
        }
    };
    
    // تحميل الرسائل من API عند فتح المحادثة
    useEffect(() => {
        console.log('=== useEffect TRIGGERED ===');
        console.log('localConversation?.id:', localConversation?.id);
        console.log('messages:', messages);
        console.log('messages?.result?.data?.length:', messages?.result?.data?.length);
        
        if (localConversation?.id && messages?.result?.data?.length > 0) {
            console.log('=== LOADING API MESSAGES ===');
            console.log('messages.result.data:', messages.result.data);
            
            // تحويل الرسائل من API إلى التنسيق المطلوب
            const apiMessages = messages.result.data.map(msg => ({
                id: `api_${msg.id}`,
                message: msg.message,
                sender_id: msg.by_me === 1 ? currentUser?.id : localConversation.receiver_id,
                created_at: msg.created_at,
                timestamp: new Date(msg.created_at).getTime(),
                by_me: msg.by_me,
                user_name: msg.user_name,
                user_image: msg.user_image
            }));
            
            console.log('Converted API messages:', apiMessages);
            
            // دمج الرسائل بدون تكرار (append بدل overwrite)
            setAllMessages(prev => {
                const ids = new Set(prev.map(m => m.id));
                const merged = [...prev, ...apiMessages.filter(m => !ids.has(m.id))];
                const sorted = merged.sort((a, b) => a.timestamp - b.timestamp);
                console.log('Final merged messages:', sorted);
                return sorted;
            });
        }
    }, [localConversation?.id, messages, currentUser?.id]);
    
    // Auto-scroll عند إضافة رسائل جديدة
    useEffect(() => {
        if (allMessages.length > 0) {
            // تأخير بسيط للتأكد من أن الرسائل تم عرضها
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        }
    }, [allMessages]);
    
    // Auto-scroll عند تغيير وضع fullscreen
    useEffect(() => {
        if (isChatFullscreen && allMessages.length > 0) {
            // تأخير أطول في fullscreen mode
            setTimeout(() => {
                scrollToBottom();
            }, 300);
        }
    }, [isChatFullscreen, allMessages]);

    // إغلاق الـ sidebar عند النقر خارجه
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isSidebarOpen && window.innerWidth <= 768) {
                const sidebar = document.querySelector(`.${styles.sidebar}`);
                const toggle = document.querySelector(`.${styles.mobileToggle}`);
                if (sidebar && !sidebar.contains(event.target) && toggle && !toggle.contains(event.target)) {
                    closeSidebar();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSidebarOpen]);

    // إدارة Firebase listener عند تغيير المحادثة
    useEffect(() => {
        let unsubscribe;
        if (currentConversation?.id) {
            unsubscribe = startFirebaseListener(currentConversation.id);
        }
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [currentConversation?.id]);
    

    // فلترة المدرسين حسب البحث
    const filteredTeachers = teachers.filter(teacher => 
        teacher.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.subject?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // فلترة المدرسين المشتركين فقط
    const subscribedTeachers = filteredTeachers.filter(teacher => teacher.is_subscribed === true);

    // فلترة الطلاب حسب البحث
    const filteredStudents = teacherStudents.filter(student => 
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.class_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSendMessage = async () => {
        if (message.trim() && localConversation?.id && currentUser?.id) {
            try {
                const text = message.trim();
    
                // حفظ الرسالة في API
                await dispatch(sendMessage({ 
                    conversation_id: localConversation.id, 
                    message: text 
                })).unwrap();
    
                // إضافة الرسالة إلى Firebase
                await addMessageToFirebase(localConversation.id, currentUser.id, text);
    
            setMessage('');
                
                // Auto-scroll للرسالة الجديدة
                setTimeout(() => {
                    scrollToBottom();
                }, 200);
                
            } catch (error) {
                console.error('خطأ في إرسال الرسالة:', error);
            }
        }
    };
    

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };
    
    const toggleFullscreen = () => {
        setIsChatFullscreen(!isChatFullscreen);
        
        // Auto-scroll عند تغيير وضع fullscreen
        if (allMessages.length > 0) {
            setTimeout(() => {
                scrollToBottom();
            }, 500);
        }
    };

    // Firebase real-time functions
    const startFirebaseListener = (conversationId) => {
        if (!conversationId) return;
        
        setIsFirebaseListening(true);
        setFirebaseError(null);
    
        const messagesRef = ref(database, `conversations/${conversationId}/messages`);
    
        const unsubscribe = onValue(messagesRef, (snapshot) => {
            const firebaseMsgs = [];
            snapshot.forEach((child) => {
                const msg = child.val();
                if (msg) firebaseMsgs.push(msg);
            });
            
            // دمج الرسائل من API + Firebase مع تجنب التكرار
            setAllMessages(prevMessages => {
                // إزالة الرسائل القديمة من Firebase (إذا كانت موجودة)
                const apiMessages = prevMessages.filter(msg => !msg.id.startsWith('firebase_'));
                
                // إضافة الرسائل الجديدة من Firebase مع تجنب التكرار
                const newFirebaseMessages = firebaseMsgs.map(msg => {
                    const firebaseId = `firebase_${msg.id || Date.now()}_${Math.random()}`;
                    
                    // التحقق من عدم وجود الرسالة مسبقاً
                    const existingMessage = apiMessages.find(apiMsg => 
                        apiMsg.message === msg.message && 
                        apiMsg.sender_id === msg.sender_id &&
                        Math.abs(new Date(apiMsg.created_at) - new Date(msg.created_at)) < 5000 // 5 ثواني
                    );
                    
                    if (existingMessage) {
                        return null; // تجاهل الرسالة المكررة
                    }
                    
                    return {
                        ...msg,
                        id: firebaseId
                    };
                }).filter(Boolean); // إزالة الرسائل null
                
                // دمج الرسائل وترتيبها حسب الوقت
                const allMsgs = [...apiMessages, ...newFirebaseMessages];
                return allMsgs.sort((a, b) => {
                    const timeA = a.timestamp || new Date(a.created_at).getTime();
                    const timeB = b.timestamp || new Date(b.created_at).getTime();
                    return timeA - timeB;
                });
            });
        }, (error) => {
            setFirebaseError(error.message);
        });
    
        // return unsubscribe function
        return () => {
            off(messagesRef);
            setIsFirebaseListening(false);
        };
    };
    



    const addMessageToFirebase = async (conversationId, senderId, message) => {
        try {
            const messagesRef = ref(database, `conversations/${conversationId}/messages`);
            const newMessageRef = push(messagesRef);
            
            const messageData = {
                id: newMessageRef.key,
                sender_id: senderId,
                message: message,
                timestamp: serverTimestamp(),
                created_at: new Date().toISOString(),
                read: false
            };
            
            await set(newMessageRef, messageData);
            
            // لا نضيف الرسالة هنا لأن Firebase listener سيقوم بذلك
            // لتجنب التكرار - الرسالة ستظهر من Firebase listener فقط
            
            return newMessageRef.key;
        } catch (error) {
            setFirebaseError(error.message);
            throw error;
        }
    };



    const handleSelectPerson = async (person) => {
        // إغلاق الـ sidebar في الشاشات الصغيرة
        if (window.innerWidth <= 768) {
            closeSidebar();
        }
        
        // إعادة تعيين pagination state
        setCurrentPage(1);
        setHasMore(true);
        
        if (currentUser?.role === 'teacher') {
            setSelectedStudent(person);
            setSelectedTeacher(null);
            // إنشاء محادثة جديدة مع الطالب
            try {
                const result = await dispatch(makeConversation({ receiver_id: person.id })).unwrap();
                
                if (result && result.result) {
                    // استخراج conversation_id من النتيجة
                    const conversationId = result.result;
                    
                    if (conversationId) {
                        // حفظ conversation_id للاستخدام في sendMessage
                        setLocalConversation({ id: conversationId, receiver_id: person.id });
                        
                        // جلب الرسائل القديمة من API
                        await dispatch(getConversation({ conversation_id: conversationId }));
                        
                        // بدء Firebase listener
                        startFirebaseListener(conversationId);
                    }
                }
            } catch (error) {
                console.error('خطأ في إنشاء المحادثة:', error);
            }
        } else {
            setSelectedTeacher(person);
            setSelectedStudent(null);
            // إنشاء محادثة جديدة مع المعلم
            try {
                const result = await dispatch(makeConversation({ receiver_id: person.id })).unwrap();
                
                if (result && result.result) {
                    // استخراج conversation_id من النتيجة
                    const conversationId = result.result;
                    
                    if (conversationId) {
                        // حفظ conversation_id للاستخدام في sendMessage
                        setLocalConversation({ id: conversationId, receiver_id: person.id });
                        
                        // جلب الرسائل القديمة من API
                        await dispatch(getConversation({ conversation_id: conversationId }));
                        
                        // بدء Firebase listener
                        startFirebaseListener(conversationId);
                    }
                }
            } catch (error) {
                console.error('خطأ في إنشاء المحادثة:', error);
            }
        }
    };

    // إذا كان المستخدم معلم، عرض صفحة "تحدث مع طلابك"
    if (currentUser?.role === 'teacher') {
        return (
            <div className={`${styles.container} ${isDarkMode ? styles.darkMode : ''}`} style={{padding : '140px 0'}}>
                {/* Header للمعلم */}
                <div className={`${styles.header} ${isDarkMode ? styles.darkMode : ''}`}>
                    <div className={styles.headerContent}>
                        <div className={styles.titleSection}>
                            <FaComments className={styles.headerIcon} />
                            <h1>تحدث مع طلابك</h1>
                            <p>تواصل مباشر مع طلابك في منصة علمني</p>
                        </div>
                        <div className={styles.stats}>
                            <div className={styles.stat}>
                                <FaUsers className={styles.statIcon} />
                                <span>{filteredStudents.length} طالب متاح</span>
                            </div>
                            <div className={styles.stat}>
                                <FaCircle className={styles.statIcon} style={{color: '#28a745'}} />
                                <span>متصل الآن</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.mainContent}>
                    {/* Mobile Toggle Button */}
                    <button 
                        className={`${styles.mobileToggle} ${isDarkMode ? styles.darkMode : ''}`}
                        onClick={toggleSidebar}
                    >
                        {isSidebarOpen ? <FaTimes /> : <FaBars />}
                    </button>

                    {/* Sidebar - قائمة الطلاب */}
                    <div className={`${styles.sidebar} ${isDarkMode ? styles.darkMode : ''} ${isSidebarOpen ? styles.mobileOpen : ''}`}>
                        <div className={styles.searchSection}>
                            <div className={styles.searchBox}>
                                <FaSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="ابحث عن طالب أو فصل..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className={styles.searchInput}
                                />
                            </div>
                        </div>

                        <div className={styles.tabs}>
                            <button className={`${styles.tab} ${styles.activeTab}`}>
                                <FaUsers className={styles.tabIcon} />
                                جميع الطلاب
                            </button>
                        </div>

                        <div className={styles.teachersList}>
                            {studentsLoading ? (
                                <div className={styles.loading}>جاري التحميل...</div>
                            ) : studentsError ? (
                                <div className={styles.error}>حدث خطأ في تحميل البيانات</div>
                            ) : filteredStudents.length === 0 ? (
                                <div className={styles.noTeachers}>لا يوجد طلاب متاحين حالياً</div>
                            ) : (
                                filteredStudents.map(student => (
                                    <div
                                        key={student.id}
                                        className={`${styles.teacherCard} ${selectedStudent?.id === student.id ? styles.selectedTeacher : ''}`}
                                        onClick={() => handleSelectPerson(student)}
                                    >
                                        <div className={styles.teacherAvatar}>
                                            <img 
                                                src={student.image || student.avatar || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"} 
                                                alt={student.name} 
                                            />
                                            <div className={`${styles.onlineStatus} ${styles.available}`}></div>
                                        </div>
                                        <div className={styles.teacherInfo}>
                                            <h4>{student.name || "اسم الطالب"}</h4>
                                            <p className={styles.subject}>{student.class_name || "الفصل"}</p>
                                            <div className={styles.teacherStats}>
                                                <span className={styles.rating}>
                                                    <FaStar className={styles.starIcon} />
                                                    {student.rating || "4.5"}
                                                </span>
                                            </div>
                                            <p className={styles.lastSeen}>
                                                متاح للتواصل
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Chat Area للمعلم */}
                    <div className="container-fluid p-0">
                        <div className={`${styles.chatArea} ${isDarkMode ? styles.darkMode : ''} ${isChatFullscreen ? styles.fullscreen : ''}`}>
                        {selectedStudent ? (
                            <>
                                {/* Chat Header */}
                                                                <div className={styles.chatHeader}>
                                    <div className={styles.chatTeacherInfo}>
                                        <img 
                                            src={selectedStudent.image || selectedStudent.avatar || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"} 
                                            alt={selectedStudent.name} 
                                        />
                                        <div>
                                            <h3>{selectedStudent.name || "اسم الطالب"}</h3>
                                            <p>{selectedStudent.subject_name || "المادة"}</p>
                                        </div>
                                    </div>
                                    <div className={styles.chatHeaderControls}>
                                        <div className={styles.chatStatus}>
                                            <span className={`${styles.statusDot} ${styles.available}`}></span>
                                            متاح للتواصل
                                        </div>
                                        <button 
                                            className={styles.fullscreenButton}
                                            onClick={toggleFullscreen}
                                            title={isChatFullscreen ? "تصغير الشات" : "تكبير الشات"}
                                        >
                                            {isChatFullscreen ? <FaCompress /> : <FaExpand />}
                                        </button>

                                    </div>
                                </div>

                                {/* Chat Messages */}
                                <div className={styles.messagesContainer} ref={messagesContainerRef}>
                                    {makeConversationLoading ? (
                                        <div className={styles.loading}>جاري إنشاء المحادثة...</div>
                                    ) : makeConversationError ? (
                                        <div className={styles.error}>خطأ في إنشاء المحادثة: {makeConversationError}</div>
                                    ) : conversationLoading ? (
                                        <div className={styles.loading}>جاري تحميل الرسائل...</div>
                                    ) : conversationError ? (
                                        <div className={styles.error}>خطأ في تحميل الرسائل: {conversationError}</div>
                                    ) : (
                                        <>
                                            {/* زر تحميل المزيد */}
                                            {hasMore && allMessages.length > 0 && (
                                                <button 
                                                    onClick={loadMoreMessages}
                                                    className={styles.loadMoreButton}
                                                    disabled={conversationLoading}
                                                >
                                                    {conversationLoading ? 'جاري التحميل...' : 'تحميل المزيد من الرسائل'}
                                                </button>
                                            )}
                                            
                                            {allMessages.length > 0 ? (
                                    allMessages.map(msg => {
                                        const isMyMessage = msg.by_me === 1 || msg.sender_id === currentUser?.id;
                                        const senderName = isMyMessage ? (currentUser?.name || msg.user_name || 'أنا') : (selectedStudent?.name || selectedTeacher?.name || 'المستخدم');
                                        const senderImage = isMyMessage ? (currentUser?.avatar || '/user.png') : (selectedStudent?.avatar || selectedTeacher?.avatar || '/user.png');
                                        

                                        return (
                                            <div key={msg.id} className={`${styles.message} ${isMyMessage ? styles.studentMessage : styles.teacherMessage}`}>
                                                <div className={styles.senderInfo}>
                                                    <img 
                                                        src={senderImage} 
                                                        alt={senderName} 
                                                        className={styles.senderAvatar}
                                                        onError={(e) => {
                                                            e.target.src = '/student.png';
                                                        }}
                                                    />
                                                    <span className={styles.senderName}>{senderName}</span>
                                                </div>
                                                <div className={styles.messageContent}>
                                                    <p>{msg.message}</p>
                                                    <span className={styles.messageTime}>
                                                        {msg.created_at 
                                                            ? new Date(msg.created_at).toLocaleTimeString('ar-EG') 
                                                            : new Date().toLocaleTimeString('ar-EG')}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className={styles.emptyChat}>
                                        <FaComments className={styles.emptyIcon} />
                                        <h3>ابدأ المحادثة مع {selectedStudent?.name || "الطالب"}</h3>
                                        <p>تواصل مباشرة عبر هذه الصفحة</p>
                                    </div>
                                )}
                                    </>
                                )}
                            </div>

                                                            {/* Message Input */}
                            <form 
                                className={styles.messageInput}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                            >
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="اكتب رسالتك هنا..."
                                    className={styles.input}
                                    rows="3"
                                    disabled={sendMessageLoading}
                                />
                                <button
                                    type="submit"
                                    disabled={!message.trim() || sendMessageLoading}
                                    className={styles.sendButton}
                                >
                                    {sendMessageLoading ? 'جاري الإرسال...' : <FaPaperPlane />}
                                </button>
                            </form>
                                {sendMessageError && (
                                    <div className={styles.error}>خطأ في إرسال الرسالة: {sendMessageError}</div>
                                )}
                            </>
                        ) : (
                            <div className={styles.noTeacherSelected}>
                                <FaComments className={styles.noTeacherIcon} />
                                <h3>اختر طالباً للبدء في المحادثة</h3>
                                <p>اختر من القائمة الجانبية للتواصل مع طالبك المفضل</p>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.container} ${isDarkMode ? styles.darkMode : ''}`} style={{padding : '140px 0'}}>
            {/* Header للطالب وولي الأمر */}
            <div className={`${styles.header} ${isDarkMode ? styles.darkMode : ''}`}>
                <div className={styles.headerContent}>
                    <div className={styles.titleSection}>
                        <FaComments className={styles.headerIcon} />
                        <h1>تحدث مع معلمك</h1>
                        <p>تواصل مباشر مع أفضل المدرسين في منصة علمني</p>
                    </div>
                    <div className={styles.stats}>
                        <div className={styles.stat}>
                            <FaGraduationCap className={styles.statIcon} />
                            <span>{subscribedTeachers.length} مدرس متاح</span>
                        </div>
                        <div className={styles.stat}>
                            <FaCircle className={styles.statIcon} style={{color: '#28a745'}} />
                            <span>{subscribedTeachers.length} مدرس مشترك</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container-fluid">
            <div className={styles.mainContent}>
                    {/* Mobile Toggle Button */}
                    <button 
                        className={`${styles.mobileToggle} ${isDarkMode ? styles.darkMode : ''}`}
                        onClick={toggleSidebar}
                    >
                        {isSidebarOpen ? <FaTimes /> : <FaBars />}
                    </button>

                {/* Sidebar - قائمة المدرسين */}
                    <div className={`${styles.sidebar} ${isDarkMode ? styles.darkMode : ''} ${isSidebarOpen ? styles.mobileOpen : ''}`}>
                    <div className={styles.searchSection}>
                        <div className={styles.searchBox}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="ابحث عن مدرس أو مادة..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>
                    </div>

                    <div className={styles.tabs}>
                        <button className={`${styles.tab} ${styles.activeTab}`}>
                            <FaGraduationCap className={styles.tabIcon} />
                            جميع المدرسين
                        </button>
                    </div>

                    <div className={styles.teachersList}>
                        {loading ? (
                            <div className={styles.loading}>جاري التحميل...</div>
                        ) : error ? (
                            <div className={styles.error}>حدث خطأ في تحميل البيانات</div>
                        ) : subscribedTeachers.length === 0 ? (
                            <div className={styles.noTeachers}>لا يوجد مدرسين متاحين حالياً</div>
                        ) : (
                            subscribedTeachers.map(teacher => (
                            <div
                                key={teacher.id}
                                className={`${styles.teacherCard} ${selectedTeacher?.id === teacher.id ? styles.selectedTeacher : ''}`}
                                    onClick={() => handleSelectPerson(teacher)}
                            >
                                <div className={styles.teacherAvatar}>
                                        <img 
                                            src={teacher.image || teacher.avatar || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"} 
                                            alt={teacher.name} 
                                        />
                                        <div className={`${styles.onlineStatus} ${styles.available}`}></div>
                                </div>
                                <div className={styles.teacherInfo}>
                                        <h4>{teacher.name || "اسم المدرس"}</h4>
                                    <div className={styles.teacherStats}>
                                        <span className={styles.rating}>
                                            <FaStar className={styles.starIcon} />
                                                {teacher.rating || "4.5"}
                                        </span>
                                        <span className={styles.students}>
                                                {teacher.students || "0"} طالب
                                        </span>
                                    </div>
                                    <p className={styles.lastSeen}>
                                            متاح للتواصل
                                    </p>
                                </div>
                            </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Chat Area للطالب وولي الأمر */}
                <div className="container-fluid p-0">
                    <div className={`${styles.chatArea} ${isDarkMode ? styles.darkMode : ''} ${isChatFullscreen ? styles.fullscreen : ''}`}>
                    {selectedTeacher ? (
                        <>
                            {/* Chat Header */}
                            <div className={styles.chatHeader}>
                                <div className={styles.chatTeacherInfo}>
                                    <img 
                                        src={selectedTeacher.image || selectedTeacher.avatar || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"} 
                                        alt={selectedTeacher.name} 
                                    />
                                    <div>
                                        <h3>{selectedTeacher.name || "اسم المدرس"}</h3>
                                        <p>{selectedTeacher.students || "0"} طالب</p>
                                    </div>
                                </div>
                                <div className={styles.chatHeaderControls}>
                                <div className={styles.chatStatus}>
                                        <span className={`${styles.statusDot} ${styles.available}`}></span>
                                        متاح للتواصل
                                    </div>
                                    <button 
                                        className={styles.fullscreenButton}
                                        onClick={toggleFullscreen}
                                        title={isChatFullscreen ? "تصغير الشات" : "تكبير الشات"}
                                    >
                                        {isChatFullscreen ? <FaCompress /> : <FaExpand />}
                                    </button>
                                </div>
                            </div>

                            {/* Chat Messages */}
                            <div className={styles.messagesContainer} ref={messagesContainerRef}>
                                {makeConversationLoading ? (
                                    <div className={styles.loading}>جاري إنشاء المحادثة...</div>
                                ) : makeConversationError ? (
                                    <div className={styles.error}>خطأ في إنشاء المحادثة: {makeConversationError}</div>
                                ) : conversationLoading ? (
                                    <div className={styles.loading}>جاري تحميل الرسائل...</div>
                                ) : conversationError ? (
                                    <div className={styles.error}>خطأ في تحميل الرسائل: {conversationError}</div>
                                ) : (
                                    <>
                                        {/* زر تحميل المزيد */}
                                        {hasMore && allMessages.length > 0 && (
                                            <button 
                                                onClick={loadMoreMessages}
                                                className={styles.loadMoreButton}
                                                disabled={conversationLoading}
                                            >
                                                {conversationLoading ? 'جاري التحميل...' : 'تحميل المزيد من الرسائل'}
                                            </button>
                                        )}
                                        
                                        {allMessages.length > 0 ? (
                                            allMessages.map(msg => {
                                                const isMyMessage = msg.by_me === 1 || msg.sender_id === currentUser?.id;
                                                const senderName = isMyMessage ? (currentUser?.name || msg.user_name || 'أنا') : (selectedTeacher?.name || 'المعلم');
                                                const senderImage = isMyMessage ? (currentUser?.avatar || '/user.png') : (selectedTeacher?.avatar || '/teacher.png');
                                                
                                                return (
                                        <div
                                            key={msg.id}
                                                        className={`${styles.message} ${isMyMessage ? styles.studentMessage : styles.teacherMessage}`}
                                                    >
                                                        <div className={styles.senderInfo}>
                                                            <img 
                                                                src={senderImage} 
                                                                alt={senderName}
                                                                className={styles.senderAvatar}
                                                                onError={(e) => {
                                                                    e.target.src = '/teacher.png';
                                                                }}
                                                            />
                                                            <span className={styles.senderName}>{senderName}</span>
                                                        </div>
                                            <div className={styles.messageContent}>
                                                            <p>{msg.message}</p>
                                                            <span className={styles.messageTime}>
                                                                {msg.created_at 
                                                                    ? new Date(msg.created_at).toLocaleTimeString('ar-EG')
                                                                    : new Date().toLocaleTimeString('ar-EG')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className={styles.emptyChat}>
                                                <FaComments className={styles.emptyIcon} />
                                                <h3>ابدأ المحادثة مع {selectedTeacher.name || "المدرس"}</h3>
                                                <p>اسأل عن أي شيء تريد فهمه في {selectedTeacher.subject || "المادة"}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Message Input */}
                            <form 
                                className={styles.messageInput}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                            >
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="اكتب رسالتك هنا..."
                                    className={styles.input}
                                    rows="3"
                                    disabled={sendMessageLoading}
                                />
                                <button
                                    type="submit"
                                    disabled={!message.trim() || sendMessageLoading}
                                    className={styles.sendButton}
                                >
                                    {sendMessageLoading ? 'جاري الإرسال...' : <FaPaperPlane />}
                                </button>
                            </form>
                            {sendMessageError && (
                                <div className={styles.error}>خطأ في إرسال الرسالة: {sendMessageError}</div>
                            )}
                        </>
                    ) : (
                        <div className={styles.noTeacherSelected}>
                            <FaComments className={styles.noTeacherIcon} />
                            <h3>اختر مدرساً للبدء في المحادثة</h3>
                            <p>اختر من القائمة الجانبية للتواصل مع مدرسك المفضل</p>
                        </div>
                    )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatWithTeacher;
